'use client';

import { useCallback, useEffect, useRef } from 'react';
import { SPARTAN_ENGINE_SECTION_IDS } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/section-slider/constants';
import type { SectionEnterSource, SectionGestureInput } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/shared/types';
import { SPARTAN_ENGINE_HERO_BEHAVIOUR } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/tracking';
import { trackDynamicEvents } from '@/tag-manager';
type HeroBehaviourAction = (typeof SPARTAN_ENGINE_HERO_BEHAVIOUR.action)[keyof typeof SPARTAN_ENGINE_HERO_BEHAVIOUR.action];
type HeroRenderMode = (typeof SPARTAN_ENGINE_HERO_BEHAVIOUR.renderMode)[keyof typeof SPARTAN_ENGINE_HERO_BEHAVIOUR.renderMode];
interface UseHeroBehaviourTrackingOptions {
  isReady: boolean;
  isLocked: boolean;
  activeIndex: number;
  renderMode: HeroRenderMode;
}
type BlockedCounts = Record<SectionGestureInput, number>;
const emptyBlockedCounts = (): BlockedCounts => ({
  wheel: 0,
  touch: 0,
  key: 0
});
const secondsSince = (startedAt: number) =>
// A string, so a zero still reaches the dataLayer.
String(Math.max(0, Math.round((Date.now() - startedAt) / 1000)));
const sectionIdOf = (index: number) => SPARTAN_ENGINE_SECTION_IDS[index];
export const useHeroBehaviourTracking = ({
  isReady,
  isLocked,
  activeIndex,
  renderMode
}: UseHeroBehaviourTrackingOptions) => {
  const renderModeRef = useRef(renderMode);
  const heroStartedAtRef = useRef<number | null>(null);
  const hasReleasedRef = useRef<boolean>(false);
  const hasAbandonedRef = useRef<boolean>(false);
  const visitRef = useRef<{
    index: number;
    enteredAt: number;
    blocked: BlockedCounts;
  }>({
    index: 0,
    enteredAt: 0,
    blocked: emptyBlockedCounts()
  });
  useEffect(() => {
    renderModeRef.current = renderMode;
  }, [renderMode]);
  const push = useCallback((action: HeroBehaviourAction, index: number, value: string | number, area: string = renderModeRef.current) => {
    trackDynamicEvents({
      event_name: SPARTAN_ENGINE_HERO_BEHAVIOUR.eventName,
      group_name: action,
      step_name: sectionIdOf(index),
      value,
      area
    });
  }, []);
  const flushBlocked = useCallback(() => {
    const visit = visitRef.current;
    (Object.keys(visit.blocked) as SectionGestureInput[]).forEach(input => {
      const attempts = visit.blocked[input];
      if (attempts > 0) {
        push(SPARTAN_ENGINE_HERO_BEHAVIOUR.action.blockedScroll, visit.index, attempts, input);
      }
    });
    visit.blocked = emptyBlockedCounts();
  }, [push]);
  const trackSlideEnter = useCallback((index_0: number, source: SectionEnterSource) => {
    const previousIndex = visitRef.current.index;
    flushBlocked();
    const {
      method
    } = SPARTAN_ENGINE_HERO_BEHAVIOUR;
    const enterMethod = source === 'scroll' && index_0 < previousIndex ? method.scrollBack : method[source];
    push(SPARTAN_ENGINE_HERO_BEHAVIOUR.action.slideEnter, index_0, enterMethod);
    visitRef.current = {
      index: index_0,
      enteredAt: Date.now(),
      blocked: emptyBlockedCounts()
    };
  }, [flushBlocked, push]);
  const trackBlockedGesture = useCallback((index_1: number, input_0: SectionGestureInput) => {
    const visit_0 = visitRef.current;
    if (visit_0.index !== index_1) {
      return;
    }
    visit_0.blocked[input_0] += 1;
  }, []);
  const trackSkip = useCallback(() => {
    const visit_1 = visitRef.current;
    push(SPARTAN_ENGINE_HERO_BEHAVIOUR.action.skip, visit_1.index, secondsSince(visit_1.enteredAt));
  }, [push]);

  // The first slide is never "entered" by the stepper, so the funnel's
  // opening step is sent here -- once the viewer can actually see it.
  useEffect(() => {
    if (!isReady || heroStartedAtRef.current !== null) {
      return;
    }
    const now = Date.now();
    heroStartedAtRef.current = now;
    visitRef.current.enteredAt = now;
    push(SPARTAN_ENGINE_HERO_BEHAVIOUR.action.slideEnter, visitRef.current.index, SPARTAN_ENGINE_HERO_BEHAVIOUR.method.initial);
  }, [isReady, push]);
  useEffect(() => {
    const startedAt = heroStartedAtRef.current;
    if (isLocked || hasReleasedRef.current || startedAt === null) {
      return;
    }
    hasReleasedRef.current = true;
    flushBlocked();
    push(SPARTAN_ENGINE_HERO_BEHAVIOUR.action.released, activeIndex, secondsSince(startedAt));
  }, [isLocked, activeIndex, flushBlocked, push]);

  // Leaving while still locked in -- a tab close, a reload, or a client-side
  // navigation away (which unmounts without a `pagehide`).
  useEffect(() => {
    const handleLeave = () => {
      flushBlocked();
      const startedAt_0 = heroStartedAtRef.current;
      if (startedAt_0 === null || hasReleasedRef.current || hasAbandonedRef.current) {
        return;
      }
      hasAbandonedRef.current = true;
      push(SPARTAN_ENGINE_HERO_BEHAVIOUR.action.abandoned, visitRef.current.index, secondsSince(startedAt_0));
    };
    window.addEventListener('pagehide', handleLeave);
    return () => {
      window.removeEventListener('pagehide', handleLeave);
      handleLeave();
    };
  }, [flushBlocked, push]);
  return {
    trackSlideEnter,
    trackBlockedGesture,
    trackSkip
  };
};